import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/manageTeamSlice";
import Cookie from "js-cookie";
import { toast } from "react-toastify";
import {
  fetchPreferredPartners,
  addPreferredPartner,
  deletePreferredPartner,
  viewOrganizationById,
} from "../api";
import AddPreferredPartner from "../Modals/AddPreferedPartner";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { useTableSavedViews, TableSavedViewsControl } from "../../../TableSavedViews";

// ✅ CONSTANTS AT TOP
const TABLE_HEADERS = ["Selection", "Action", "Name", "Phone", "Email", "Fax", "Office Code"];
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const SORTABLE_COLUMNS = {
  "Name": "name",
  "Phone": "phone",
  "Email": "email",
  "Fax": "fax",
  "Office Code": "officeCode",
};

// ✅ MAIN COMPONENT
const PreferredPartners = ({ data }) => {
  const officeCode = Cookie.get("officeCode");
  const dispatch = useDispatch();

  // ✅ STATE DECLARATIONS
  const [partners, setPartners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [ascending, setAscending] = useState(true);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // ✅ useTableSavedViews Hook (for column customization)
  const tableViews = useTableSavedViews({
    pageKey: "preferred-partners-list",
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2, // Selection, Action are always visible
    defaultStickyColumnKeys: ["Selection", "Action", "Name"],
    getExtraConfig: () => ({
      currentPage: page,
      pageSize: pageSize,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setPageSize(config.pageSize);
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // ✅ REFS
  const dropdownRef = useRef(null);
  const checkboxHeaderRef = useRef(null);

  // ✅ API CALL - Load Partners
  const loadPartners = async () => {
    try {
      const res = await fetchPreferredPartners(officeCode);
      setPartners(Array.isArray(res?.data) ? res.data : []);
    } catch {
      console.error("Failed to fetch preferred partners.");
    }
  };

  // ✅ USEEFFECT HOOKS
  useEffect(() => {
    if (data !== null && data !== undefined) {
      setPartners(data);
    } else {
      loadPartners();
    }
  }, [data]);

  useEffect(() => {
    loadPartners();
  }, [officeCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ✅ EVENT HANDLERS

  const handleSort = (columnHeader) => {
    if (sortBy === columnHeader) {
      setAscending(!ascending);
    } else {
      setSortBy(columnHeader);
      setAscending(true);
    }
    setPage(1);
    markDirty();
  };

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === id ? null : id));
  };

  const handleSelectAll = (checked) =>
    setSelected(checked ? partners.map((p) => p.officeCode) : []);

  const handleSingleCheckbox = (code) =>
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const handleDelete = async (partnerCode, name) => {
    try {
      await deletePreferredPartner(officeCode, partnerCode);
      toast.success(`${name || partnerCode} removed from preferred partners`);
      loadPartners();
    } catch {
      toast.error("Failed to delete partner.");
    }
  };

  const handleAdd = async (name) => {
    if (!name?.trim()) return;
    try {
      await addPreferredPartner(officeCode, name.trim());
      setShowForm(false);
      loadPartners();
    } catch {
      toast.error("Failed to add partner.");
    }
  };

  const handleRowDoubleClick = async (partner) => {
    try {
      const orgId = partner.org_id;
      if (!orgId) {
        toast.warning("Organization ID not found for this partner.");
        return;
      }
      const res = await viewOrganizationById(orgId);
      const orgData = res.data;
      dispatch(addTab({
        id: `view-partner-${orgId}`,
        name: `Organization ${partner.name}`,
        contentType: "viewPreferredPartnerDetails",
        data: orgData,
        isDynamic: true,
        createdAt: Date.now(),
      }));
    } catch (err) {
      console.error("Failed to load organization details:", err);
      toast.error("Failed to fetch organization details.");
    }
  };

  // ✅ SORTING LOGIC
  let effectivePartners = partners;

  if (sortBy && SORTABLE_COLUMNS[sortBy]) {
    const sortKey = SORTABLE_COLUMNS[sortBy];
    effectivePartners = [...partners].sort((a, b) => {
      let aVal = a?.[sortKey];
      let bVal = b?.[sortKey];

      if (!aVal || !bVal) return 0;

      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();

      if (aVal < bVal) return ascending ? -1 : 1;
      if (aVal > bVal) return ascending ? 1 : -1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(effectivePartners.length / pageSize));
  const paged = effectivePartners.slice((page - 1) * pageSize, page * pageSize);

  // ✅ RETURN JSX
  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {/* Empty State */}
      {partners.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-gray-500 text-lg font-semibold">No partners found</p>
          <p className="text-sm text-gray-400 mt-2">Add preferred partners to get started</p>
        </div>
      )}

      {/* Table Section */}
      {partners.length > 0 && (
        <>
          {/* Column Views Control */}
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={null}
              isDirty={isDirty}
              allDataHeaders={TABLE_HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          </div>

          {/* Table Container */}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              headers={TABLE_HEADERS}
              data={paged}
              initialColumnWidths={savedColumnWidths}
              onColumnResize={onColumnResize}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              selectedRows={selected}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              nonHideableColumnIds={["Selection", "Action", "Name"]}
              sortBy={sortBy}
              ascending={ascending}
              onSort={handleSort}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
            >
              {(p, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
                const id = p.officeCode;
                const isOpen = openDropdownRow === id;

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                      style={{
                        width: "80px",
                        minWidth: "80px",
                        maxWidth: "80px",
                        boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="cursor-pointer mx-auto block"
                        checked={selected.includes(p.officeCode)}
                        onChange={() => handleSingleCheckbox(p.officeCode)}
                      />
                    </td>

                    {/* Column 1 — Action */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                      style={{
                        width: "50px",
                        minWidth: "50px",
                        maxWidth: "50px",
                        boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                      }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen &&
                      ReactDOM.createPortal(
                        <div
                          ref={dropdownRef}
                          style={{
                            position: "absolute",
                            top: dropdownPos.top,
                            left: dropdownPos.left,
                            zIndex: 9999,
                          }}
                        >
                          <ActionMenu
                            rowId={id}
                            isOpen
                            onClose={() => setOpenDropdownRow(null)}
                            moduleName="PreferredPartner"
                            actions={[
                              {
                                icon: "Eye",
                                label: "View Partner",
                                color: "blue",
                                action: () => handleRowDoubleClick(p),
                              },
                              {
                                icon: "Trash",
                                label: "Remove Partner",
                                color: "red",
                                action: () => handleDelete(p.officeCode, p.name),
                              },
                            ]}
                          />
                        </div>,
                        document.body
                      )}

                    {/* Data Columns — respects visibleColumns for pin/hide/reorder */}
                    {(() => {
                      const orderedDataHeaders = (visibleColumns ?? [])
                        .map((col) => col.id)
                        .filter((id) => id !== "Selection" && id !== "Action");
                      const dataHeadersToRender = orderedDataHeaders.length
                        ? orderedDataHeaders
                        : dataHeaders;

                      return dataHeadersToRender.map((header) => {
                        const { className: colClass, style: colStyle } = getStickyCellProps(header);

                        let content;
                        switch (header) {
                          case "Name":
                            content = p.name || "-";
                            break;
                          case "Phone":
                            content = p.phone || "-";
                            break;
                          case "Email":
                            content = p.email || "-";
                            break;
                          case "Fax":
                            content = p.fax || "-";
                            break;
                          case "Office Code":
                            content = p.officeCode || "-";
                            break;
                          default:
                            content = "-";
                        }

                        return (
                          <td
                            key={header}
                            className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                            style={{
                              ...colStyle,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            onDoubleClick={() => handleRowDoubleClick(p)}
                          >
                            {content}
                          </td>
                        );
                      });
                    })()}
                  </>
                );
              }}
            </Table>
          </div>

          {/* Pagination */}
          <GlobalPagination
            label="Partners per page"
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            options={PAGE_SIZE_OPTIONS}
          />
        </>
      )}

      {/* Add Button */}
      <button
        className="fixed bottom-8 right-8 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition"
        onClick={() => setShowForm(true)}
        title="Add Preferred Partner"
      >
        <svg
          className="w-7 h-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Modal */}
      {showForm && (
        <AddPreferredPartner onClose={() => setShowForm(false)} onSubmit={handleAdd} />
      )}
    </div>
  );
};

export default PreferredPartners;